import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';

// ── helpers ──────────────────────────────────────────────────────────────────

const generateUuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const TABS = [
  { key: 'query-types', label: 'Query Types' },
  { key: 'query-flows', label: 'Query Flows' },
  { key: 'routing', label: 'Routing' },
];

const ENGINE_OPTIONS = [
  { value: 'any', label: 'Any' },
  { value: 'solr', label: 'Solr' },
  { value: 'opensearch', label: 'OpenSearch' },
  { value: 'shadow', label: 'Shadow' },
];

// ── Query Types tab ───────────────────────────────────────────────────────────

const BLANK_TYPE = () => ({
  id: generateUuid(),
  name: '',
  label: '',
  target: 'ahri',
  search_engine: 'any',
  def_type: 'edismax',
  query_fields: [],
  phrase_fields: [],
  boost: '',
  facets: [],
  rows: 20,
});

function QueryTypesTab({ cfg, setCfg, saveCfg, saving }) {
  const [engineTab, setEngineTab] = useState('all');
  const [draft, setDraft] = useState(null);
  const [draftMode, setDraftMode] = useState(null); // 'add' | 'edit'
  const [draftIdx, setDraftIdx] = useState(null);
  const [draftError, setDraftError] = useState('');

  const queryTypes = useMemo(() => Array.isArray(cfg?.query_types) ? cfg.query_types : [], [cfg]);

  const visibleRows = useMemo(() => {
    if (engineTab === 'all') return queryTypes;
    return queryTypes.filter((r) => String(r?.search_engine || 'any').toLowerCase() === engineTab);
  }, [queryTypes, engineTab]);

  const openAdd = () => {
    setDraft(BLANK_TYPE());
    setDraftMode('add');
    setDraftIdx(null);
    setDraftError('');
  };

  const openEdit = (idx) => {
    const entry = queryTypes[idx];
    if (!entry) return;
    setDraft({ ...entry });
    setDraftMode('edit');
    setDraftIdx(idx);
    setDraftError('');
  };

  const closeDraft = () => { setDraft(null); setDraftMode(null); setDraftIdx(null); setDraftError(''); };

  const saveDraft = async () => {
    if (!draft?.name?.trim()) { setDraftError('Name is required.'); return; }
    let next;
    if (draftMode === 'add') {
      next = [...queryTypes, draft];
    } else {
      next = queryTypes.map((r, i) => (i === draftIdx ? draft : r));
    }
    const ok = await saveCfg({ query_types: next });
    if (ok) { setCfg((prev) => ({ ...prev, query_types: next })); closeDraft(); }
    else setDraftError('Failed to save. Please try again.');
  };

  const removeType = async (idx) => {
    if (!window.confirm('Delete this query type?')) return;
    const next = queryTypes.filter((_, i) => i !== idx);
    const ok = await saveCfg({ query_types: next });
    if (ok) setCfg((prev) => ({ ...prev, query_types: next }));
  };

  const cloneType = async (idx) => {
    const source = queryTypes[idx];
    if (!source) return;
    const cloned = { ...source, id: generateUuid(), name: `${source.name}_copy` };
    const next = [...queryTypes, cloned];
    const ok = await saveCfg({ query_types: next });
    if (ok) setCfg((prev) => ({ ...prev, query_types: next }));
  };

  const setField = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));

  const engineCounts = useMemo(() => {
    const counts = { all: queryTypes.length };
    ENGINE_OPTIONS.forEach(({ value }) => {
      counts[value] = queryTypes.filter((r) => String(r?.search_engine || 'any').toLowerCase() === value).length;
    });
    return counts;
  }, [queryTypes]);

  return (
    <div>
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-3">
        <div>
          <div className="fw-semibold">Search Types</div>
          <div className="text-muted small">Manage reusable query profiles for AHRI and Product search.</div>
        </div>
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={openAdd}>
          Add New
        </button>
      </div>

      {/* Engine filter tabs */}
      <ul className="nav nav-tabs mb-3">
        {[{ key: 'all', label: 'All' }, ...ENGINE_OPTIONS.map((o) => ({ key: o.value, label: o.label }))].map((tab) => (
          <li className="nav-item" key={tab.key}>
            <button
              type="button"
              className={`nav-link${engineTab === tab.key ? ' active' : ''}`}
              onClick={() => setEngineTab(tab.key)}
            >
              {tab.label} ({engineCounts[tab.key] ?? 0})
            </button>
          </li>
        ))}
      </ul>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-sm align-middle">
          <thead>
            <tr>
              <th>Name</th>
              <th>Label</th>
              <th>Target</th>
              <th>Engine</th>
              <th>Parser</th>
              <th style={{ width: 200 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 ? (
              <tr><td colSpan={6} className="text-muted text-center py-3">No search types for this engine.</td></tr>
            ) : (
              visibleRows.map((entry) => {
                const idx = queryTypes.indexOf(entry);
                return (
                  <tr key={entry?.id || idx}>
                    <td className="fw-semibold">{entry?.name || '-'}</td>
                    <td>{entry?.label || '-'}</td>
                    <td className="text-uppercase">{entry?.target || 'ahri'}</td>
                    <td className="text-uppercase">{entry?.search_engine || 'any'}</td>
                    <td>{entry?.def_type || 'edismax'}</td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button type="button" className="btn btn-outline-secondary" onClick={() => openEdit(idx)}>Edit</button>
                        <button type="button" className="btn btn-outline-primary" onClick={() => cloneType(idx)}>Copy</button>
                        <button type="button" className="btn btn-outline-danger" onClick={() => removeType(idx)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Add / Edit drawer */}
      {draft && (
        <div className="workflow-drawer-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeDraft(); }}>
          <div className="workflow-drawer">
            <div className="workflow-drawer__header">
              <div className="fw-semibold">{draftMode === 'add' ? 'Add Search Type' : 'Edit Search Type'}</div>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={closeDraft}>Close</button>
            </div>
            <div className="workflow-drawer__body">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">ID</label>
                  <input className="form-control" value={draft.id} readOnly />
                  <div className="form-text">Auto-generated stable identifier.</div>
                </div>
                <div className="col-12">
                  <label className="form-label">Target</label>
                  <select className="form-select" value={draft.target} onChange={(e) => setField('target', e.target.value)}>
                    <option value="ahri">AHRI</option>
                    <option value="product">Product</option>
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">Search engine</label>
                  <select className="form-select" value={draft.search_engine} onChange={(e) => setField('search_engine', e.target.value)}>
                    {ENGINE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">Unique name <span className="text-danger">*</span></label>
                  <input className="form-control" value={draft.name} onChange={(e) => setField('name', e.target.value)} />
                  <div className="form-text">Used to reference this profile in flows.</div>
                </div>
                <div className="col-12">
                  <label className="form-label">Display label</label>
                  <input className="form-control" value={draft.label} onChange={(e) => setField('label', e.target.value)} />
                </div>
                <div className="col-12">
                  <label className="form-label">Parser (def_type)</label>
                  <select className="form-select" value={draft.def_type} onChange={(e) => setField('def_type', e.target.value)}>
                    <option value="edismax">edismax</option>
                    <option value="dismax">dismax</option>
                    <option value="lucene">lucene</option>
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">Query fields (one per line)</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={Array.isArray(draft.query_fields) ? draft.query_fields.join('\n') : draft.query_fields || ''}
                    onChange={(e) => setField('query_fields', e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))}
                    placeholder="e.g. name^2&#10;description"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Phrase fields (one per line)</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={Array.isArray(draft.phrase_fields) ? draft.phrase_fields.join('\n') : draft.phrase_fields || ''}
                    onChange={(e) => setField('phrase_fields', e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Boost query</label>
                  <input className="form-control" value={draft.boost || ''} onChange={(e) => setField('boost', e.target.value)} />
                </div>
                <div className="col-12">
                  <label className="form-label">Rows (page size)</label>
                  <input type="number" className="form-control" value={draft.rows ?? 20} onChange={(e) => setField('rows', Number(e.target.value))} />
                </div>
              </div>
            </div>
            <div className="workflow-drawer__footer">
              {draftError && <div className="text-danger small mb-2">{draftError}</div>}
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-primary btn-sm" onClick={saveDraft} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={closeDraft}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Query Flows tab ───────────────────────────────────────────────────────────

const NODE_ID = () => `node_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

const BLANK_FLOW = () => ({ id: generateUuid(), name: 'New Flow', search_engine: 'any', nodes: [], edges: [] });

function QueryFlowsTab({ cfg, setCfg, saveCfg, saving }) {
  const [view, setView] = useState('list');  // 'list' | 'editor'
  const [currentId, setCurrentId] = useState('');
  const [flowName, setFlowName] = useState('');
  const [flowEngine, setFlowEngine] = useState('any');
  const [steps, setSteps] = useState([]);
  const [selectedStep, setSelectedStep] = useState('');
  const [saveError, setSaveError] = useState('');

  const flowList = useMemo(() => {
    const raw = cfg?.system_shop_query_flows;
    if (!Array.isArray(raw)) return [];
    return raw.filter((f) => f && typeof f === 'object' && f.id);
  }, [cfg]);

  const openFlow = (flowId) => {
    const flow = flowList.find((f) => f.id === flowId);
    if (!flow) return;
    setCurrentId(flow.id);
    setFlowName(flow.name || 'Untitled Flow');
    setFlowEngine(String(flow.search_engine || 'any').toLowerCase());
    // flatten nodes as steps for editing
    setSteps(Array.isArray(flow.nodes) ? flow.nodes.map((n) => ({ ...n })) : []);
    setSelectedStep('');
    setSaveError('');
    setView('editor');
  };

  const createFlow = () => {
    const flow = BLANK_FLOW();
    const next = [...flowList, flow];
    setCfg((prev) => ({ ...prev, system_shop_query_flows: next }));
    setCurrentId(flow.id);
    setFlowName(flow.name);
    setFlowEngine(flow.search_engine);
    setSteps([]);
    setSelectedStep('');
    setSaveError('');
    setView('editor');
  };

  const deleteFlow = async (flowId) => {
    if (!window.confirm('Delete this flow?')) return;
    const next = flowList.filter((f) => f.id !== flowId);
    const ok = await saveCfg({ system_shop_query_flows: next });
    if (ok) setCfg((prev) => ({ ...prev, system_shop_query_flows: next }));
  };

  const saveCurrentFlow = async () => {
    if (!currentId) return;
    const next = flowList.map((f) =>
      f.id === currentId
        ? { ...f, name: flowName || 'Untitled Flow', search_engine: flowEngine || 'any', nodes: steps }
        : f
    );
    const ok = await saveCfg({ system_shop_query_flows: next });
    if (ok) {
      setCfg((prev) => ({ ...prev, system_shop_query_flows: next }));
      setSaveError('');
    } else {
      setSaveError('Failed to save flow.');
    }
  };

  const addStep = (type) => {
    const base = { id: NODE_ID(), type, isStart: steps.length === 0, next: '', nextTrue: '', nextFalse: '' };
    const step =
      type === 'query' ? { ...base, queryType: '', queryTypeId: '', pageSize: 20 }
      : type === 'condition' ? { ...base, operator: '>=', threshold: 1, conditionType: 'count', field: '', fieldOperator: 'exists', fieldValue: '' }
      : { ...base, flowId: '' };
    setSteps((prev) => [...prev, step]);
    setSelectedStep(step.id);
  };

  const removeStep = (id) => setSteps((prev) => prev.filter((s) => s.id !== id));

  const updateStep = (id, field, value) =>
    setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)));

  const currentStep = steps.find((s) => s.id === selectedStep);

  const queryTypeOptions = useMemo(
    () => Array.isArray(cfg?.query_types) ? cfg.query_types : [],
    [cfg?.query_types]
  );

  return (
    <div>
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-3">
        <div>
          <div className="fw-semibold">Query Flow Designer</div>
          <div className="text-muted small">Create query flows that branch by search results.</div>
        </div>
        <div className="d-flex flex-wrap gap-2">
          {view === 'editor' ? (
            <>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setView('list')}>← Back to list</button>
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={saveCurrentFlow} disabled={saving || !currentId}>
                {saving ? 'Saving…' : 'Save Flow'}
              </button>
            </>
          ) : (
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={createFlow}>Add New Flow</button>
          )}
        </div>
      </div>

      {view === 'list' && (
        <div className="border rounded p-3">
          {flowList.length === 0 ? (
            <div className="text-muted small">No flows yet. Click Add New Flow to start.</div>
          ) : (
            <div className="row g-3">
              {flowList.map((flow) => (
                <div key={flow.id} className="col-12 col-md-6 col-xl-4">
                  <div className="border rounded p-3 h-100">
                    <div className="fw-semibold">{flow.name}</div>
                    <div className="text-muted small mb-2">
                      Engine: {flow.search_engine || 'any'} · Steps: {Array.isArray(flow.nodes) ? flow.nodes.length : 0}
                    </div>
                    <div className="btn-group btn-group-sm">
                      <button className="btn btn-outline-secondary" onClick={() => openFlow(flow.id)}>Edit</button>
                      <button className="btn btn-outline-danger" onClick={() => deleteFlow(flow.id)}>Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === 'editor' && (
        <div className="row g-3">
          {/* Flow name + engine */}
          <div className="col-12 col-lg-6">
            <label className="form-label">Flow name</label>
            <input className="form-control" value={flowName} onChange={(e) => setFlowName(e.target.value)} placeholder="Flow name" />
          </div>
          <div className="col-12 col-lg-3">
            <label className="form-label">Engine</label>
            <select className="form-select" value={flowEngine} onChange={(e) => setFlowEngine(e.target.value)}>
              {ENGINE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Steps list */}
          <div className="col-12 col-lg-6">
            <div className="border rounded p-3" style={{ maxHeight: 500, overflowY: 'auto' }}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="fw-semibold small">Steps</div>
                <div className="btn-group btn-group-sm">
                  <button className="btn btn-outline-primary" onClick={() => addStep('query')}>+ Query</button>
                  <button className="btn btn-outline-primary" onClick={() => addStep('condition')}>+ Condition</button>
                  <button className="btn btn-outline-primary" onClick={() => addStep('flow')}>+ Flow</button>
                </div>
              </div>
              {steps.length === 0 && <div className="text-muted small">Add steps to build the flow.</div>}
              {steps.map((step, idx) => (
                <div
                  key={step.id}
                  className={`border rounded p-2 mb-2 ${selectedStep === step.id ? 'border-primary bg-primary bg-opacity-10' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedStep(step.id)}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="small fw-semibold">
                      {idx + 1}. {step.type === 'condition' ? 'Condition' : step.type === 'flow' ? 'Flow' : 'Query'}
                      {step.isStart && <span className="badge bg-primary ms-1" style={{ fontSize: '0.6rem' }}>Start</span>}
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      style={{ padding: '0 0.4rem', fontSize: '0.7rem' }}
                      onClick={(e) => { e.stopPropagation(); removeStep(step.id); }}
                    >×</button>
                  </div>
                  {step.type === 'query' && step.queryType && <div className="text-muted" style={{ fontSize: '0.75rem' }}>{step.queryType}</div>}
                </div>
              ))}
            </div>
          </div>

          {/* Step detail editor */}
          <div className="col-12 col-lg-6">
            {currentStep ? (
              <div className="border rounded p-3">
                <div className="fw-semibold small mb-3">Edit step: {currentStep.type}</div>
                <div className="row g-2">
                  <div className="col-12">
                    <label className="form-label small">Start step</label>
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="step-start" checked={!!currentStep.isStart}
                        onChange={(e) => updateStep(currentStep.id, 'isStart', e.target.checked)} />
                      <label className="form-check-label small" htmlFor="step-start">Is start step</label>
                    </div>
                  </div>
                  {currentStep.type === 'query' && (
                    <>
                      <div className="col-12">
                        <label className="form-label small">Query type</label>
                        <select className="form-select form-select-sm" value={currentStep.queryType || ''}
                          onChange={(e) => {
                            const found = queryTypeOptions.find((q) => q.name === e.target.value);
                            updateStep(currentStep.id, 'queryType', e.target.value);
                            updateStep(currentStep.id, 'queryTypeId', found?.id || '');
                          }}>
                          <option value="">Select a query type</option>
                          {queryTypeOptions.map((q) => <option key={q.id} value={q.name}>{q.name}</option>)}
                        </select>
                      </div>
                      <div className="col-12">
                        <label className="form-label small">Page size</label>
                        <input type="number" className="form-control form-control-sm" value={currentStep.pageSize ?? 20}
                          onChange={(e) => updateStep(currentStep.id, 'pageSize', Number(e.target.value))} />
                      </div>
                    </>
                  )}
                  {currentStep.type === 'condition' && (
                    <>
                      <div className="col-6">
                        <label className="form-label small">Condition type</label>
                        <select className="form-select form-select-sm" value={currentStep.conditionType || 'count'}
                          onChange={(e) => updateStep(currentStep.id, 'conditionType', e.target.value)}>
                          <option value="count">Count</option>
                          <option value="field">Field</option>
                        </select>
                      </div>
                      <div className="col-3">
                        <label className="form-label small">Operator</label>
                        <select className="form-select form-select-sm" value={currentStep.operator || '>='}
                          onChange={(e) => updateStep(currentStep.id, 'operator', e.target.value)}>
                          {['>=', '>', '=', '<', '<='].map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </div>
                      <div className="col-3">
                        <label className="form-label small">Threshold</label>
                        <input type="number" className="form-control form-control-sm" value={currentStep.threshold ?? 1}
                          onChange={(e) => updateStep(currentStep.id, 'threshold', Number(e.target.value))} />
                      </div>
                    </>
                  )}
                  {currentStep.type === 'flow' && (
                    <div className="col-12">
                      <label className="form-label small">Sub-flow</label>
                      <select className="form-select form-select-sm" value={currentStep.flowId || ''}
                        onChange={(e) => updateStep(currentStep.id, 'flowId', e.target.value)}>
                        <option value="">Select a flow</option>
                        {flowList.filter((f) => f.id !== currentId).map((f) => (
                          <option key={f.id} value={f.id}>{f.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="col-12">
                    <label className="form-label small">Next step ID (on success / default)</label>
                    <input className="form-control form-control-sm" value={currentStep.next || ''}
                      onChange={(e) => updateStep(currentStep.id, 'next', e.target.value)} placeholder="leave blank = end" />
                  </div>
                  {(currentStep.type === 'condition' || currentStep.type === 'query') && (
                    <>
                      <div className="col-12">
                        <label className="form-label small">Next step if true</label>
                        <input className="form-control form-control-sm" value={currentStep.nextTrue || ''}
                          onChange={(e) => updateStep(currentStep.id, 'nextTrue', e.target.value)} />
                      </div>
                      <div className="col-12">
                        <label className="form-label small">Next step if false</label>
                        <input className="form-control form-control-sm" value={currentStep.nextFalse || ''}
                          onChange={(e) => updateStep(currentStep.id, 'nextFalse', e.target.value)} />
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="border rounded p-3 text-muted small d-flex align-items-center justify-content-center h-100">
                Select a step to edit its properties.
              </div>
            )}
          </div>

          {saveError && <div className="col-12"><div className="alert alert-danger py-2">{saveError}</div></div>}
        </div>
      )}
    </div>
  );
}

// ── Routing tab ───────────────────────────────────────────────────────────────

const BLANK_ROUTE = (order = 1) => ({
  id: null,
  route_name: '',
  route_order: order,
  enabled: true,
  stop_on_match: true,
  query_flow_id: '',
  query_flow_name: '',
  when_clause_text: '{}',
  input_mapping_text: '{}',
});

function RoutingTab({ cfg }) {
  const { getAuthHeaders } = useAuth();
  const headers = useMemo(() => ({ 'Content-Type': 'application/json', ...getAuthHeaders() }), [getAuthHeaders]);

  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [view, setView] = useState('list');
  const [draft, setDraft] = useState(null);
  const [formError, setFormError] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [routeTest, setRouteTest] = useState({ input: '{\n  "queryText": ""\n}', loading: false, error: '', result: null });

  const flowList = useMemo(
    () => (Array.isArray(cfg?.system_shop_query_flows) ? cfg.system_shop_query_flows : []),
    [cfg]
  );

  const loadRoutes = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/system-shop/routes`, { headers: getAuthHeaders() });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.detail || `HTTP ${resp.status}`);
      setRoutes(Array.isArray(data?.routes) ? data.routes : []);
    } catch (err) {
      setError(String(err?.message || err));
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { loadRoutes(); }, [loadRoutes]);

  const openEditor = (route = null) => {
    setFormError('');
    if (!route) {
      const nextOrder = routes.length ? Math.max(...routes.map((r) => Number(r.route_order || 0))) + 1 : 1;
      setDraft(BLANK_ROUTE(nextOrder));
    } else {
      setDraft({
        id: route.id,
        route_name: route.route_name || '',
        route_order: Number(route.route_order || 0),
        enabled: !!route.enabled,
        stop_on_match: route.stop_on_match !== false,
        query_flow_id: route.query_flow_id || '',
        query_flow_name: route.query_flow_name || '',
        when_clause_text: JSON.stringify(route.when_clause || {}, null, 2),
        input_mapping_text: JSON.stringify(route.input_mapping || {}, null, 2),
      });
    }
    setRouteTest({ input: '{\n  "queryText": ""\n}', loading: false, error: '', result: null });
    setView('editor');
  };

  const set = (field, value) => setDraft((prev) => (prev ? { ...prev, [field]: value } : prev));

  const saveRoute = async () => {
    if (!draft) return;
    const routeName = String(draft.route_name || '').trim();
    if (!routeName) { setFormError('Route name is required.'); return; }
    if (!draft.query_flow_id) { setFormError('Query flow is required.'); return; }
    let whenClause = {};
    let inputMapping = {};
    try { whenClause = draft.when_clause_text ? JSON.parse(draft.when_clause_text) : {}; }
    catch { setFormError('Invalid JSON in conditions.'); return; }
    try { inputMapping = draft.input_mapping_text ? JSON.parse(draft.input_mapping_text) : {}; }
    catch { setFormError('Invalid JSON in input mapping.'); return; }

    const payload = {
      route_name: routeName,
      route_order: Number(draft.route_order || 0),
      enabled: !!draft.enabled,
      query_flow_id: draft.query_flow_id,
      query_flow_name: draft.query_flow_name || '',
      when_clause: whenClause,
      input_mapping: inputMapping,
      stop_on_match: !!draft.stop_on_match,
    };
    const url = draft.id
      ? `${API_BASE_URL}/api/v1/ahri/system-shop/routes/${draft.id}`
      : `${API_BASE_URL}/api/v1/ahri/system-shop/routes`;
    setLoading(true);
    try {
      const resp = await fetch(url, { method: draft.id ? 'PUT' : 'POST', headers, body: JSON.stringify(payload) });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.detail || `HTTP ${resp.status}`);
      await loadRoutes();
      setView('list');
      setDraft(null);
    } catch (err) {
      setFormError(String(err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  const deleteRoute = async (routeId) => {
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/system-shop/routes/${routeId}`, { method: 'DELETE', headers: getAuthHeaders() });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.detail || `HTTP ${resp.status}`);
      setDeleteId(null);
      await loadRoutes();
    } catch (err) {
      setError(String(err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  const runTest = async () => {
    setRouteTest((prev) => ({ ...prev, loading: true, error: '', result: null }));
    let payload = {};
    try { payload = routeTest.input ? JSON.parse(routeTest.input) : {}; }
    catch { setRouteTest((prev) => ({ ...prev, loading: false, error: 'Invalid JSON payload.' })); return; }
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/system-shop/routes/execute`, {
        method: 'POST', headers, body: JSON.stringify(payload),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.detail || `HTTP ${resp.status}`);
      setRouteTest((prev) => ({ ...prev, loading: false, result: data }));
    } catch (err) {
      setRouteTest((prev) => ({ ...prev, loading: false, error: String(err?.message || err) }));
    }
  };

  return (
    <div>
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-3">
        <div>
          <div className="fw-semibold">Routing Engine</div>
          <div className="text-muted small">Evaluate routes in order, map inputs, and execute query flows.</div>
        </div>
        <div className="d-flex flex-wrap gap-2">
          {view === 'editor' ? (
            <>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => setView('list')}>← Back to list</button>
              <button className="btn btn-outline-primary btn-sm" onClick={saveRoute} disabled={loading || !draft}>
                {loading ? 'Saving…' : 'Save Route'}
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-outline-primary btn-sm" onClick={() => openEditor(null)}>Add Route</button>
              <button className="btn btn-outline-secondary btn-sm" onClick={loadRoutes} disabled={loading}>Refresh</button>
            </>
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}

      {view === 'list' && (
        <div className="border rounded p-3">
          {loading && <div className="text-muted small mb-2">Loading routes…</div>}
          {!loading && routes.length === 0 && <div className="text-muted small">No routes yet. Click Add Route to start.</div>}
          <div className="row g-3">
            {routes.map((route) => (
              <div key={`route-${route.id}`} className="col-12 col-md-6 col-xl-4">
                <div className="border rounded p-3 h-100">
                  <div className="fw-semibold">{route.route_name || 'Untitled route'}</div>
                  <div className="text-muted small mb-2">Flow: {route.query_flow_name || route.query_flow_id || '-'}</div>
                  <div className="d-flex flex-wrap gap-1 mb-2">
                    <span className="badge bg-light text-dark border">Order {route.route_order ?? 0}</span>
                    <span className={`badge ${route.enabled ? 'bg-success' : 'bg-secondary'}`}>{route.enabled ? 'Enabled' : 'Disabled'}</span>
                    <span className="badge bg-light text-dark border">{route.stop_on_match === false ? 'Continue' : 'Stop on match'}</span>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => openEditor(route)}>Edit</button>
                    {deleteId === route.id ? (
                      <>
                        <button className="btn btn-outline-danger btn-sm" onClick={() => deleteRoute(route.id)}>Confirm delete</button>
                        <button className="btn btn-outline-secondary btn-sm" onClick={() => setDeleteId(null)}>Cancel</button>
                      </>
                    ) : (
                      <button className="btn btn-outline-danger btn-sm" onClick={() => setDeleteId(route.id)}>Delete</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {view === 'editor' && draft && (
        <div className="row g-3">
          <div className="col-12 col-lg-7">
            <div className="border rounded p-3">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Route name <span className="text-danger">*</span></label>
                  <input className="form-control" value={draft.route_name} onChange={(e) => set('route_name', e.target.value)} placeholder="Route name" />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">Route order</label>
                  <input type="number" className="form-control" value={draft.route_order} onChange={(e) => set('route_order', e.target.value)} />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">Query flow <span className="text-danger">*</span></label>
                  <select className="form-select" value={draft.query_flow_id}
                    onChange={(e) => {
                      const selected = flowList.find((f) => f.id === e.target.value);
                      set('query_flow_id', e.target.value);
                      set('query_flow_name', selected?.name || '');
                    }}>
                    <option value="">Select a flow</option>
                    {flowList.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                  {flowList.length === 0 && <div className="form-text">Create a query flow first.</div>}
                </div>
                <div className="col-12">
                  <div className="d-flex flex-wrap gap-4">
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="route-enabled" checked={!!draft.enabled} onChange={(e) => set('enabled', e.target.checked)} />
                      <label className="form-check-label" htmlFor="route-enabled">Enabled</label>
                    </div>
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="route-stop" checked={!!draft.stop_on_match} onChange={(e) => set('stop_on_match', e.target.checked)} />
                      <label className="form-check-label" htmlFor="route-stop">Stop on match</label>
                    </div>
                  </div>
                </div>
                {formError && <div className="col-12"><div className="alert alert-danger py-2 mb-0">{formError}</div></div>}
                <div className="col-12">
                  <label className="form-label">Conditions (when_clause JSON)</label>
                  <textarea className="form-control font-monospace" rows={7} value={draft.when_clause_text}
                    onChange={(e) => set('when_clause_text', e.target.value)} />
                  <div className="form-text">{"Example: { \"all\": [{ \"field\": \"input.queryText\", \"op\": \"contains\", \"value\": \"acme\" }] }"}</div>
                </div>
                <div className="col-12">
                  <label className="form-label">Input mapping (input_mapping JSON)</label>
                  <textarea className="form-control font-monospace" rows={7} value={draft.input_mapping_text}
                    onChange={(e) => set('input_mapping_text', e.target.value)} />
                  <div className="form-text">{"Example: { \"query\": { \"from\": \"input.queryText\", \"default\": \"\" } }"}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-5">
            <div className="border rounded p-3 h-100">
              <div className="fw-semibold mb-2">Test routing</div>
              <label className="form-label small">Input payload</label>
              <textarea className="form-control font-monospace" rows={6} value={routeTest.input}
                onChange={(e) => setRouteTest((prev) => ({ ...prev, input: e.target.value }))} />
              <div className="form-text mb-2">{"Example: { \"queryText\": \"101-085NL\" }"}</div>
              {routeTest.error && <div className="alert alert-danger py-2 small">{routeTest.error}</div>}
              <button className="btn btn-outline-primary btn-sm" onClick={runTest} disabled={routeTest.loading}>
                {routeTest.loading ? 'Running…' : 'Run Test'}
              </button>
              {routeTest.result && (
                <pre className="mt-3 p-2 border rounded bg-light small" style={{ maxHeight: 340, overflow: 'auto' }}>
                  {JSON.stringify(routeTest.result, null, 2)}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const OrderGenieWorkflowSetup = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'query-types';

  const { getAuthHeaders } = useAuth();
  const headers = useMemo(() => ({ 'Content-Type': 'application/json', ...getAuthHeaders() }), [getAuthHeaders]);

  const [cfg, setCfg] = useState(null);
  const [cfgLoading, setCfgLoading] = useState(true);
  const [cfgError, setCfgError] = useState('');
  const [saving, setSaving] = useState(false);
  const cfgRef = useRef(null);

  // Keep a ref to latest cfg so saveCfg always uses fresh data
  useEffect(() => { cfgRef.current = cfg; }, [cfg]);

  const loadCfg = useCallback(async () => {
    setCfgLoading(true);
    setCfgError('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/config`, { headers: getAuthHeaders() });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.detail || `HTTP ${resp.status}`);
      setCfg(data?.config || {});
    } catch (err) {
      setCfgError(String(err?.message || 'Failed to load configuration.'));
    } finally {
      setCfgLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { loadCfg(); }, [loadCfg]);

  // Merge partial updates into the full config, then PUT
  const saveCfg = useCallback(async (partial) => {
    const current = cfgRef.current || {};
    const merged = { ...current, ...partial };
    setSaving(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/config`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(merged),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.detail || `HTTP ${resp.status}`);
      setCfg(merged);
      return true;
    } catch {
      return false;
    } finally {
      setSaving(false);
    }
  }, [headers]);

  const setTab = (tab) => setSearchParams({ tab }, { replace: true });

  return (
    <div className="order-genie-workflow-page">
      {/* Page header */}
      <div className="page-header d-flex align-items-center gap-3 px-4 py-3 border-bottom bg-white">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <ArrowLeft size={15} />
          <span>Back</span>
        </button>
        <div>
          <h5 className="mb-0 fw-semibold">Workflow Setup</h5>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0" style={{ fontSize: '0.78rem' }}>
              <li className="breadcrumb-item text-muted">OrderX</li>
              <li className="breadcrumb-item active">Workflow Setup</li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-bottom bg-white px-4">
        <ul className="nav nav-tabs border-0">
          {TABS.map((tab) => (
            <li className="nav-item" key={tab.key}>
              <button
                type="button"
                className={`nav-link${activeTab === tab.key ? ' active fw-semibold' : ''}`}
                onClick={() => setTab(tab.key)}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Content */}
      <div className="p-4">
        {cfgLoading && <div className="text-muted small">Loading configuration…</div>}
        {cfgError && (
          <div className="alert alert-danger">
            {cfgError}
            <button type="button" className="btn btn-sm btn-outline-danger ms-3" onClick={loadCfg}>Retry</button>
          </div>
        )}
        {!cfgLoading && !cfgError && cfg && (
          <>
            {activeTab === 'query-types' && (
              <QueryTypesTab cfg={cfg} setCfg={setCfg} saveCfg={saveCfg} saving={saving} />
            )}
            {activeTab === 'query-flows' && (
              <QueryFlowsTab cfg={cfg} setCfg={setCfg} saveCfg={saveCfg} saving={saving} />
            )}
            {activeTab === 'routing' && (
              <RoutingTab cfg={cfg} />
            )}
          </>
        )}
      </div>

      <style>{`
        /* Drawer overlay */
        .workflow-drawer-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.35);
          z-index: 1300;
          display: flex;
          justify-content: flex-end;
        }
        .workflow-drawer {
          width: min(480px, 92vw);
          background: #fff;
          display: flex;
          flex-direction: column;
          height: 100%;
          box-shadow: -4px 0 24px rgba(0,0,0,0.15);
        }
        .workflow-drawer__header {
          padding: 1rem 1.25rem;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .workflow-drawer__body {
          flex: 1;
          overflow-y: auto;
          padding: 1.25rem;
        }
        .workflow-drawer__footer {
          padding: 1rem 1.25rem;
          border-top: 1px solid #e2e8f0;
        }
        @media (max-width: 576px) {
          .workflow-drawer { width: 100vw; }
        }
      `}</style>
    </div>
  );
};

export default OrderGenieWorkflowSetup;
